import { z } from 'zod';
import { defineEndpoint } from '../define';

export const SUPPORTED_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'application/pdf',
] as const;

export const ContentTypeSchema = z.enum(SUPPORTED_CONTENT_TYPES);
export type SupportedContentType = z.infer<typeof ContentTypeSchema>;

export const DeviceFileGroupSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  allowedContentTypes: z.array(ContentTypeSchema),
  perDeviceCap: z.number().int().min(1).max(100),
  sortOrder: z.number().int(),
  isSystem: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DeviceFileGroup = z.infer<typeof DeviceFileGroupSchema>;

// List-only projection: adds how many devices currently have files in the group.
// Kept separate from DeviceFileGroupSchema, which is embedded in device detail.
export const DeviceFileGroupListItemSchema = DeviceFileGroupSchema.extend({
  deviceCount: z.number().int().min(0),
});
export type DeviceFileGroupListItem = z.infer<typeof DeviceFileGroupListItemSchema>;

export const ListDeviceFileGroupsResponseSchema = z.object({
  data: z.array(DeviceFileGroupListItemSchema),
});

export const listDeviceFileGroups = defineEndpoint({
  method: 'GET',
  path: '/v1/device-file-groups',
  params: z.object({}),
  query: z.object({}),
  body: z.undefined(),
  response: ListDeviceFileGroupsResponseSchema,
});

const slugSchema = z.string().regex(/^[a-z][a-z0-9-]{1,39}$/);
const nameSchema = z.string().trim().min(1).max(60);
const allowedTypesSchema = z.array(ContentTypeSchema).min(1);
const capSchema = z.number().int().min(1).max(100);

export const CreateDeviceFileGroupBodySchema = z.object({
  slug: slugSchema,
  name: nameSchema,
  allowedContentTypes: allowedTypesSchema,
  perDeviceCap: capSchema.default(10),
  sortOrder: z.number().int().optional(),
});
export type CreateDeviceFileGroupBody = z.infer<typeof CreateDeviceFileGroupBodySchema>;

export const CreateDeviceFileGroupResponseSchema = z.object({ data: DeviceFileGroupSchema });

export const createDeviceFileGroup = defineEndpoint({
  method: 'POST',
  path: '/v1/device-file-groups',
  params: z.object({}),
  query: z.object({}),
  body: CreateDeviceFileGroupBodySchema,
  response: CreateDeviceFileGroupResponseSchema,
  successStatus: 201,
});

export const UpdateDeviceFileGroupBodySchema = z
  .object({
    slug: slugSchema.optional(),
    name: nameSchema.optional(),
    allowedContentTypes: allowedTypesSchema.optional(),
    perDeviceCap: capSchema.optional(),
    sortOrder: z.number().int().optional(),
  })
  .refine((b) => Object.values(b).some((v) => v !== undefined), {
    message: 'At least one editable field must be present',
  });

export const updateDeviceFileGroup = defineEndpoint({
  method: 'PATCH',
  path: '/v1/device-file-groups/:groupId',
  params: z.object({ groupId: z.string().uuid() }),
  query: z.object({}),
  body: UpdateDeviceFileGroupBodySchema,
  response: CreateDeviceFileGroupResponseSchema,
});

export const deleteDeviceFileGroup = defineEndpoint({
  method: 'DELETE',
  path: '/v1/device-file-groups/:groupId',
  params: z.object({ groupId: z.string().uuid() }),
  query: z.object({}),
  body: z.undefined(),
  response: z.object({}),
  successStatus: 204,
});
